import React, { useState, useEffect, useCallback, useMemo } from 'react';
import { stageAPI } from '../services/api';
import StageModale from './StageModale';
import StageEditeurCriteres from './StageEditeurCriteres';
import StageEvaluationPublique from './StageEvaluationPublique';
import StageVisitePublique from './StageVisitePublique';
import StageEditeurDocuments from './StageEditeurDocuments';
import StageDepotPublic from './StageDepotPublic';
import StageEditeurFiche from './StageEditeurFiche';
import StageFicheLiaisonPublique from './StageFicheLiaisonPublique';
import './StagePublic.css';

/**
 * SUIVI DE STAGE — Paramétrage → Modèles (lot 3.8a)
 * ==================================================
 * Spec     : doc/modules/suivi_de_stage.md, sous-onglet « Modèles », écarts E111 → E116
 * Maquette : #psc-modeles — quatre accordéons « param-card » : Fiches de liaison,
 * Fiches de visite, Grilles d'évaluation, Documents retour. Chacun : un bouton
 * « Nouveau modèle », une liste, et un éditeur qui REMPLACE la liste à l'ouverture.
 *
 * ── CE QUI EST ÉDITABLE ────────────────────────────────────────────────────
 * Les quatre familles : « à critères » (visite, grille — 3.8a), documents de retour
 * (3.8b), fiche de liaison (3.8c). Une famille pas encore éditable porterait `lot` :
 * ses gestes restent présents, grisés, et NOMMENT leur lot — un bouton mort muet se
 * lit comme une panne.
 */

const FAMILLES = [
    // Lot 3.8c : la fiche de liaison devient éditable, avec son figement au 1er envoi de l'étape ① (E112).
    { cle: 'fiche_liaison', titre: 'Modèles de fiche de liaison', icone: 'fa-file-lines', editable: true },
    { cle: 'visite', titre: 'Modèles de fiche de visite', icone: 'fa-car', editable: true, criteres: true },
    { cle: 'eval', titre: "Modèles de grille d'évaluation maître de stage", icone: 'fa-star-half-stroke', editable: true, criteres: true },
    // Lot 3.8b : les documents de retour deviennent éditables, avec leur figement (E112).
    { cle: 'documents_retour', titre: 'Modèles de documents retour', icone: 'fa-file-arrow-up', editable: true }
];

/** « Utilisé dans » — et si la campagne est LANCÉE, c'est-à-dire figée (E115). */
function Usage({ liste }) {
    if (!liste || !liste.length) return <span style={{ color: '#94a3b8' }}>—</span>;
    return (
        <div className="modeles-usage">
            {liste.map((u) => (
                <span key={u.configuration_id} className={'modeles-usage-chip' + (u.lancee ? ' lancee' : '')}
                      title={u.figee ? 'Campagne lancée : elle garde la version avec laquelle elle a commencé'
                          : u.lancee ? 'Campagne lancée, mais ce modèle y est encore lu EN DIRECT : son figement arrive avec son éditeur'
                          : 'Campagne non lancée : une modification du modèle s’y appliquera'}>
                    {u.figee && <i className="fa-solid fa-lock" />} {u.libelle} · {u.figee ? 'lancée (figée)' : u.lancee ? 'lancée' : 'non lancée'}
                </span>
            ))}
        </div>
    );
}

/** L'état fictif du PORTAIL DE DÉPÔT pour l'aperçu des documents (E116) — seulement les documents actifs. */
function portailFictif(brouillon) {
    const pieces = brouillon.items.filter((x) => x.actif !== false).map((x, k) => ({
        attendu_id: `a${k}`, libelle: x.libelle || '(document sans libellé)', obligatoire: x.obligatoire !== false,
        formats: x.formats_acceptes === 'pdf' ? ['pdf'] : ['pdf', 'jpeg', 'png'], courante: null }));
    return {
        dossier: { nom: 'DUBOIS', prenom: 'Emma', formation_nom: 'BUT1 FI', semestre: 'S2', departement_nom: 'Département',
                   date_debut: '2026-04-01', date_fin: '2026-04-30' },
        convention_disponible: true, ouvert: true, message: null, pieces,
        raison_non_soumissible: pieces.some((x) => x.obligatoire) ? `Pièce(s) obligatoire(s) à déposer : ${pieces.filter((x) => x.obligatoire).map((x) => x.libelle).join(', ')}.` : 'Aucune nouvelle pièce à envoyer.',
        taille_max_octets: 10 * 1024 * 1024
    };
}

/**
 * Le contenu fictif de la FICHE DE LIAISON pour l'aperçu (E116) — sections actives,
 * filtrées sur qui les remplit, comme le serveur le fait pour la vraie page. Côté
 * maître, la fiche arrive pré-remplie de ce que « l'étudiant » a saisi.
 */
function ficheFictive(brouillon, vue) {
    const visibles = vue === 'maitre' ? ['maitre', 'les_deux'] : ['etudiant', 'les_deux'];
    const sections = (brouillon.sections || [])
        .filter((s) => s.actif !== false && visibles.includes(s.remplissage))
        .map((s, k) => ({ ...s, id: s.id || `a${k}`, titre: s.titre || '(section sans titre)' }));
    return {
        dossier: { id: 0, nom: 'DUBOIS', prenom: 'Emma', formation_nom: 'BUT1 FI', semestre: 'S2', departement_nom: 'Département',
                   date_debut: '2026-04-01', date_fin: '2026-04-30', statut: 'lien_etudiant_envoye' },
        modele: { nom: brouillon.nom, texte_intro: brouillon.texte_intro, afficher_missions: brouillon.afficher_missions === true,
                  missions_libelle: brouillon.missions_libelle, missions_placeholder: brouillon.missions_placeholder },
        sections,
        reponses: vue === 'maitre'
            ? { fiche: { entreprise_nom: 'Innovate SAS', entreprise_adresse: '12 rue des Lilas', entreprise_cp: '91000', entreprise_ville: 'Évry',
                         maitre_nom: 'MARTIN', maitre_prenom: 'Claire', maitre_fonction: 'Responsable Qualité',
                         maitre_email: 'c.martin@innovate.fr', maitre_telephone: '06 12 34 56 78' }, sections: {} }
            : { fiche: null, sections: {} }
    };
}

/** La page publique fictive d'un aperçu — les VRAIES pages, sur de fausses données (E116). */
function pageFictive(famille, brouillon) {
    let n = 0;
    const criteres = brouillon.criteres.map((c) => ({
        id: `a${++n}`, libelle: c.libelle || '(critère sans libellé)', sous_detail: c.sous_detail, commentaire_actif: c.commentaire_actif,
        niveaux: c.niveaux.map((x) => ({ id: `a${++n}`, intitule: x.intitule || '…', points: Number(x.points) || 0 }))
    }));
    return {
        dossier: { nom: 'DUBOIS', prenom: 'Emma', formation_nom: 'BUT1 FI', semestre: 'S2', departement_nom: 'Département',
                   entreprise_nom: 'Innovate SAS', date_debut: '2026-04-01', date_fin: '2026-04-30' },
        grille: { nom: brouillon.nom, masquer_points: brouillon.masquer_points, criteres,
                  total_max: criteres.reduce((s, c) => s + Math.max(0, ...c.niveaux.map((x) => x.points)), 0) }
    };
}

/**
 * L2a (E170, v87 — maquette rév. 10) : dans la vignette d'un processus (`configurationId`), « Les documents » montre
 * UN modèle par famille — la copie de ce processus — avec « Aperçu », « Repartir d'un autre modèle » et « Modifier ».
 * Les modèles d'origine du département ne s'y modifient pas : ils servent de point de départ.
 */
const dateFr = (d) => (d ? new Date(d).toLocaleDateString('fr-FR') : '');

function StageParametrageModeles({ departementId, modifiable, onModelesChange, configurationId = null }) {
    const [listes, setListes] = useState(null);
    const [docs, setDocs] = useState(null);           // L2a : { processus, familles: [{ famille, modele, sources, … }] }
    const [repartir, setRepartir] = useState(null);   // L2a : { famille, libelle, sources, choix }
    const [erreur, setErreur] = useState(null);
    const [ouverts, setOuverts] = useState({ fiche_liaison: true });   // la maquette ouvre le 1er accordéon
    const [edition, setEdition] = useState(null);     // { famille, modele }
    const [erreurEdition, setErreurEdition] = useState(null);
    const [enCours, setEnCours] = useState(false);
    const [apercu, setApercu] = useState(null);       // { famille, brouillon }
    const [aSupprimer, setASupprimer] = useState(null);
    const [vueFiche, setVueFiche] = useState('etudiant');   // aperçu de la fiche : étudiant | maître
    // E175 (22/10) : ce que l'enregistrement change VRAIMENT — { libelle, figee_le } (une campagne lancée garde sa version).
    const [enregistre, setEnregistre] = useState(null);
    // Chaque aperçu s'ouvre sur la vue ÉTUDIANT — celle qu'on reçoit en premier. Vu au banc :
    // sans cela, il se rouvrait sur la dernière vue choisie, et l'on croyait voir la page étudiant.
    const ouvrirApercu = (a) => { setVueFiche('etudiant'); setApercu(a); };
    // ⚠️ Mémorisé : un nouvel objet à chaque rendu reposerait la page d'aperçu et effacerait la saisie.
    const apercuFiche = useMemo(() => (apercu?.famille === 'fiche_liaison'
        ? { vue: vueFiche, contenu: ficheFictive(apercu.brouillon, vueFiche) } : null), [apercu, vueFiche]);

    const recharger = useCallback(async () => {
        try {
            if (configurationId) {
                const r = await stageAPI.documentsProcessus(configurationId, departementId);
                setDocs(r.data);
                setListes({});
            } else {
                const r = await stageAPI.listerModeles(departementId);
                setListes(r.data);
            }
        } catch (e) {
            setErreur(e.response?.data?.error || 'Lecture des modèles impossible');
        }
    }, [departementId, configurationId]);

    const validerRepartir = async () => {
        if (!repartir?.choix) return;
        setEnCours(true); setErreur(null);
        try {
            const r = await stageAPI.repartirModele(configurationId, repartir.famille, repartir.choix, departementId);
            setDocs(r.data);
            setRepartir(null);
            if (onModelesChange) onModelesChange();
        } catch (e) { setRepartir(null); setErreur(e.response?.data?.error || 'Le remplacement a échoué.'); }
        finally { setEnCours(false); }
    };
    useEffect(() => { recharger(); }, [recharger]);

    // ⚠️ Le rappel du parent passe par une référence d'appel direct, jamais par des
    // dépendances : c'est lui qui recharge le paramétrage (listes des configurations).
    const apresEcriture = useCallback(async () => {
        await recharger();
        if (onModelesChange) onModelesChange();
    }, [recharger, onModelesChange]);

    const ouvrir = async (famille, id) => {
        setErreur(null); setErreurEdition(null);
        if (!id) { setEdition({ famille, modele: { id: null, nom: '', masquer_points: false, criteres: [], items: [], sections: [], utilise_dans: [] } }); return; }
        try {
            const r = await stageAPI.lireModele(famille, id, departementId);
            setEdition({ famille, modele: r.data.modele });
        } catch (e) { setErreur(e.response?.data?.error || 'Lecture du modèle impossible'); }
    };

    const enregistrer = async (brouillon) => {
        setEnCours(true); setErreurEdition(null);
        try {
            const { famille, modele } = edition;
            const r = modele.id
                ? await stageAPI.modifierModele(famille, modele.id, departementId, brouillon)
                : await stageAPI.creerModele(famille, departementId, brouillon);
            setEdition(null);
            /**
             * ⚠️ E175 — LE PIÈGE SILENCIEUX (vécu par Malika le 2026-10-22) : une campagne LANCÉE garde la version de son
             * premier envoi (E101/E112). Activer « Missions » s'enregistrait sans erreur… et ne changeait rien pour les
             * étudiants de la campagne en cours. On le dit, au lieu de laisser croire que c'est pris en compte.
             */
            const d = docs ? docs.familles.find((x) => x.famille === famille) : null;
            setEnregistre(d ? { libelle: d.libelle, figee_le: d.figee_le || null } : null);
            await apresEcriture();
            return r;
        } catch (e) {
            setErreurEdition(e.response?.data?.error || "L'enregistrement a échoué.");
        } finally { setEnCours(false); }
    };

    const dupliquer = async (famille, id) => {
        setErreur(null);
        try { await stageAPI.dupliquerModele(famille, id, departementId); await apresEcriture(); }
        catch (e) { setErreur(e.response?.data?.error || 'La duplication a échoué.'); }
    };

    const supprimer = async () => {
        const { famille, modele } = aSupprimer;
        setEnCours(true);
        try { await stageAPI.supprimerModele(famille, modele.id, departementId); setASupprimer(null); await apresEcriture(); }
        catch (e) { setASupprimer(null); setErreur(e.response?.data?.error || 'La suppression a échoué.'); }
        finally { setEnCours(false); }
    };

    const voirApercu = async (famille, id) => {
        try {
            const r = await stageAPI.lireModele(famille, id, departementId);
            ouvrirApercu({ famille, brouillon: r.data.modele, n: Date.now() });
        } catch (e) { setErreur(e.response?.data?.error || 'Lecture du modèle impossible'); }
    };

    if (!listes) {
        return erreur
            ? <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>
            : <div className="stg-attente"><i className="fa-solid fa-circle-notch stg-tourne" /><div>Lecture des modèles…</div></div>;
    }

    // ── L'aperçu : il remplace l'onglet, comme dans la maquette (« Retour aux modèles »). ──
    const vueApercu = (apercuFiche && (
        <div>
            <div className="apercu-barre">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercu(null)}>
                    <i className="fa-solid fa-arrow-left" /> {edition ? "Retour à l'éditeur" : 'Retour aux modèles'}
                </button>
                <div style={{ fontSize: 13, fontWeight: 600 }}>Aperçu — Fiche de liaison</div>
                {/* Deux pages, deux destinataires : ce que voit l'étudiant, ce que voit le maître. */}
                <div style={{ display: 'flex', gap: 4 }} role="group" aria-label="Vue de l'aperçu">
                    {[['etudiant', 'Vue étudiant'], ['maitre', 'Vue maître de stage']].map(([v, l]) => (
                        <button key={v} type="button" className={'btn btn-sm ' + (vueFiche === v ? 'btn-green' : 'btn-outline')}
                                aria-pressed={vueFiche === v} onClick={() => setVueFiche(v)}>{l}</button>
                    ))}
                </div>
                <div className="apercu-modele">
                    Modèle : {apercu.brouillon.nom || '(sans nom)'} · {apercuFiche.contenu.sections.length} section{apercuFiche.contenu.sections.length > 1 ? 's' : ''} pour {vueFiche === 'maitre' ? 'le maître' : "l'étudiant"}
                </div>
            </div>
            {/* ⚠️ `key` : changer de vue, ou rouvrir, repart d'une page vierge. */}
            <div className="apercu-cadre"><StageFicheLiaisonPublique key={`${apercu.n}-${vueFiche}`} apercu={apercuFiche} /></div>
        </div>
    )) || (apercu && apercu.famille === 'documents_retour' && (
        <div>
            <div className="apercu-barre">
                <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercu(null)}>
                    <i className="fa-solid fa-arrow-left" /> {edition ? "Retour à l'éditeur" : 'Retour aux modèles'}
                </button>
                <div style={{ fontSize: 13, fontWeight: 600 }}>
                    Aperçu — Portail dépôt documents <span style={{ color: 'var(--stg-muted)', fontWeight: 400 }}>(vue étudiant)</span>
                </div>
                <div className="apercu-modele">
                    Modèle : {apercu.brouillon.nom || '(sans nom)'} · {apercu.brouillon.items.filter((x) => x.actif !== false).length} documents
                </div>
            </div>
            <div className="apercu-cadre"><StageDepotPublic key={apercu.n} apercu={portailFictif(apercu.brouillon)} /></div>
        </div>
    )) || (apercu && (() => {
        const page = pageFictive(apercu.famille, apercu.brouillon);
        return (
            <div>
                <div className="apercu-barre">
                    <button type="button" className="btn btn-outline btn-sm" onClick={() => setApercu(null)}>
                        <i className="fa-solid fa-arrow-left" /> {edition ? "Retour à l'éditeur" : 'Retour aux modèles'}
                    </button>
                    <div style={{ fontSize: 13, fontWeight: 600 }}>
                        Aperçu — {apercu.famille === 'eval' ? "Grille d'évaluation" : 'Fiche de visite'}
                        {' '}<span style={{ color: 'var(--stg-muted)', fontWeight: 400 }}>(vue {apercu.famille === 'eval' ? 'maître de stage' : 'tuteur'})</span>
                    </div>
                    <div className="apercu-modele">
                        Modèle : {apercu.brouillon.nom || '(sans nom)'} · {page.grille.criteres.length} critère{page.grille.criteres.length > 1 ? 's' : ''}
                        {!page.grille.masquer_points && ` · /${page.grille.total_max} pts`}
                    </div>
                </div>
                <div className="apercu-cadre">
                    {/* ⚠️ `key` : chaque ouverture repart d'une page vierge, sans les choix d'un aperçu précédent. */}
                    {/* Lot 3.5c : la fiche de visite a SA page (date, mode, durée, « Remplie par ») — l'aperçu la montre (E116). */}
                    {apercu.famille === 'visite'
                        ? <StageVisitePublique key={apercu.n} apercu
                                               page={{ ...page, fiche: page.grille, commence: true, ouvert: true, courante: null,
                                                       remplie_par_defaut: 'Sophie LEROY', dossier: { ...page.dossier, tuteur: 'Sophie LEROY', maitre: 'Pierre GIRARD' } }} />
                        : <StageEvaluationPublique key={apercu.n} page={page} apercu variante="eval" />}
                </div>
            </div>
        );
    })());

    return (
        <>
            {vueApercu}
            {/* ⚠️ L'éditeur reste MONTÉ pendant l'aperçu, seulement caché : revenir de
                l'aperçu ne doit pas perdre le brouillon en cours. */}
            <div style={{ display: apercu ? 'none' : 'block' }}>
                {configurationId ? (
                    <div className="modeles-intro">
                        <i className="fa-solid fa-circle-info" /> Un modèle par famille : <b>celui de ce processus</b>. Les familles dont l'étape est
                        désactivée dans « Le processus » gardent leur modèle, sans servir. Une campagne <b>lancée</b> garde la version avec laquelle elle a commencé.
                    </div>
                ) : (
                    <div className="modeles-intro">
                        <i className="fa-solid fa-circle-info" /> Les modèles sont permanents — réutilisés chaque année. Modifiez-les si les critères ou textes évoluent.
                        {' '}Une campagne <b>lancée</b> garde la version avec laquelle elle a commencé.
                    </div>
                )}
                {erreur && <div className="stg-encart stg-encart-erreur"><i className="fa-solid fa-circle-exclamation" /><span>{erreur}</span></div>}
                {enregistre && !edition && (enregistre.figee_le ? (
                    <div className="stg-encart stg-encart-alerte" data-enregistre="figee">
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>{enregistre.libelle} : enregistré. <b>La campagne en cours garde la version de son premier envoi
                            (le {dateFr(enregistre.figee_le)})</b> : ce changement vaudra pour la prochaine campagne.</span>
                    </div>
                ) : (
                    <div className="stg-encart stg-encart-ok" data-enregistre="ok">
                        <i className="fa-solid fa-check" /><span>{enregistre.libelle} : enregistré. Les prochains envois utiliseront cette version.</span>
                    </div>
                ))}

                {FAMILLES.map((f) => {
                    const ouvert = !!ouverts[f.cle];
                    const liste = listes[f.cle] || [];
                    const enEdition = edition?.famille === f.cle;
                    const titreBloque = f.lot ? `L'éditeur de ces modèles arrive au lot ${f.lot}, avec leur figement par campagne.` : null;
                    const d = docs ? docs.familles.find((x) => x.famille === f.cle) : null;
                    if (configurationId && d) {
                        return (
                            <div className="param-card" key={f.cle} data-famille={f.cle}>
                                <div className="param-card-header">
                                    <span>
                                        <i className={'fa-solid ' + f.icone} style={{ color: 'var(--stg-vert-d)' }} /> {d.libelle}{' '}
                                        {d.modele ? <span className="pf-chip ok" style={{ marginLeft: 6 }}>{d.modele.nom}</span>
                                                  : <span className="pf-chip" style={{ marginLeft: 6 }}>aucun modèle</span>}{' '}
                                        {d.modele && <span style={{ fontSize: 11, color: 'var(--stg-muted)', fontWeight: 400 }}>{d.modele.resume}</span>}
                                        {!d.etape_active && <span className="pf-chip" style={{ marginLeft: 6 }} title="Activez l'étape dans « Le processus » pour qu'il serve">étape désactivée</span>}
                                        {d.figee_le && <span className="pf-chip" style={{ marginLeft: 6 }} title="La campagne en cours garde la version envoyée">
                                            <i className="fa-solid fa-lock" /> figée le {dateFr(d.figee_le)}</span>}
                                    </span>
                                    <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                        <button type="button" className="btn btn-outline btn-sm" disabled={!d.modele} onClick={() => voirApercu(f.cle, d.modele.id)}>
                                            <i className="fa-solid fa-eye" /> Aperçu
                                        </button>
                                        <button type="button" className="btn btn-outline btn-sm" disabled={!modifiable || !!edition || !d.sources.length}
                                                title={!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.' : undefined}
                                                onClick={() => setRepartir({ famille: f.cle, libelle: d.libelle, sources: d.sources, choix: d.sources[0]?.id || null })}>
                                            <i className="fa-solid fa-rotate-left" /> Repartir d'un autre modèle
                                        </button>
                                        <button type="button" className="btn btn-green btn-sm" disabled={!d.modele || !!edition}
                                                onClick={() => { setOuverts((o) => ({ ...o, [f.cle]: true })); ouvrir(f.cle, d.modele.id); }}>
                                            <i className="fa-solid fa-pen" /> {modifiable ? 'Modifier' : 'Voir'}
                                        </button>
                                    </div>
                                </div>
                                {enEdition && (
                                    <div className="param-card-body">
                                        {f.cle === 'fiche_liaison' ? (
                                            <StageEditeurFiche key={edition.modele.id} modele={edition.modele}
                                                modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                                onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                                onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                        ) : f.cle === 'documents_retour' ? (
                                            <StageEditeurDocuments key={edition.modele.id} modele={edition.modele}
                                                modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                                onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                                onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                        ) : (
                                            <StageEditeurCriteres key={edition.modele.id} famille={f.cle} modele={edition.modele}
                                                modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                                onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                                onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                        )}
                                    </div>
                                )}
                            </div>
                        );
                    }
                    return (
                        <div className="param-card" key={f.cle}>
                            <div className="param-card-header accordeon" onClick={() => setOuverts((o) => ({ ...o, [f.cle]: !o[f.cle] }))}
                                 role="button" aria-expanded={ouvert}>
                                <span><i className={'fa-solid ' + f.icone} style={{ color: 'var(--stg-vert-d)' }} /> {f.titre}</span>
                                <div style={{ display: 'flex', alignItems: 'center', gap: 8 }}>
                                    <button type="button" className="btn btn-green btn-sm" disabled={!f.editable || !modifiable || !!edition}
                                            title={titreBloque || (!modifiable ? 'Lecture seule : votre profil ne permet pas de modifier ce module.' : undefined)}
                                            onClick={(e) => { e.stopPropagation(); setOuverts((o) => ({ ...o, [f.cle]: true })); ouvrir(f.cle, null); }}>
                                        <i className="fa-solid fa-plus" /> Nouveau modèle
                                    </button>
                                    <i className={'fa-solid ' + (ouvert ? 'fa-chevron-up' : 'fa-chevron-down')} />
                                </div>
                            </div>
                            {ouvert && (
                                <div className="param-card-body">
                                    {enEdition && f.cle === 'fiche_liaison' ? (
                                        <StageEditeurFiche key={edition.modele.id || 'nouveau'} modele={edition.modele}
                                            modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                            onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                            onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                    ) : enEdition && f.cle === 'documents_retour' ? (
                                        <StageEditeurDocuments key={edition.modele.id || 'nouveau'} modele={edition.modele}
                                            modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                            onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                            onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                    ) : enEdition ? (
                                        <StageEditeurCriteres key={edition.modele.id || 'nouveau'} famille={f.cle} modele={edition.modele}
                                            modifiable={modifiable} enCours={enCours} erreur={erreurEdition}
                                            onEnregistrer={enregistrer} onAnnuler={() => { setEdition(null); setErreurEdition(null); }}
                                            onApercu={(brouillon) => ouvrirApercu({ famille: f.cle, brouillon, n: Date.now() })} />
                                    ) : (
                                        <>
                                            {f.lot && (
                                                <div className="stg-encart stg-encart-info">
                                                    <i className="fa-solid fa-circle-info" /><span>{titreBloque}</span>
                                                </div>
                                            )}
                                            <table>
                                                <thead>
                                                    {f.cle === 'fiche_liaison' && <tr><th>Nom du modèle</th><th>Missions</th><th>Utilisé dans</th><th>Actions</th></tr>}
                                                    {f.criteres && <tr><th>Nom du modèle</th><th>Critères</th><th>Barème total</th><th>Commentaire / critère</th><th>Utilisé dans</th><th>Actions</th></tr>}
                                                    {f.cle === 'documents_retour' && <tr><th>Nom du modèle</th><th>Documents</th><th>Utilisé dans</th><th>Actions</th></tr>}
                                                </thead>
                                                <tbody>
                                                    {!liste.length && <tr><td colSpan={6} style={{ color: 'var(--stg-muted)', textAlign: 'center' }}>Aucun modèle.</td></tr>}
                                                    {liste.map((m) => (
                                                        <tr key={m.id}>
                                                            <td><strong>{m.nom}</strong></td>
                                                            {f.cle === 'fiche_liaison' && <td><span className={'badge ' + (m.afficher_missions ? 'badge-ok' : 'badge-wait')}>{m.afficher_missions ? 'Oui' : 'Non'}</span></td>}
                                                            {f.criteres && <>
                                                                <td>{m.nb_criteres} critère{m.nb_criteres > 1 ? 's' : ''}</td>
                                                                <td>{m.masquer_points ? <span title="Les points sont masqués dans le formulaire">/{m.total} pts <i className="fa-solid fa-eye-slash" /></span> : `/${m.total} pts`}</td>
                                                                <td><span className={'badge ' + (m.commentaire ? 'badge-ok' : 'badge-wait')} style={{ fontSize: 10 }}>{m.commentaire ? 'Oui' : 'Non'}</span></td>
                                                            </>}
                                                            {f.cle === 'documents_retour' && <td style={{ fontSize: 11 }}>{m.nb_documents} docs ({m.nb_obligatoires} obligatoires)</td>}
                                                            <td><Usage liste={m.utilise_dans} /></td>
                                                            <td className="actions">
                                                                <button type="button" className="action-btn" title={titreBloque || 'Modifier'} disabled={!f.editable || !!edition}
                                                                        onClick={() => ouvrir(f.cle, m.id)}><i className="fa-solid fa-pen" /></button>
                                                                <button type="button" className="action-btn" title={titreBloque || 'Dupliquer'} disabled={!f.editable || !modifiable || !!edition}
                                                                        onClick={() => dupliquer(f.cle, m.id)}><i className="fa-solid fa-copy" /></button>
                                                                <button type="button" className="action-btn" title={titreBloque || 'Aperçu formulaire'} disabled={!f.editable}
                                                                        onClick={() => voirApercu(f.cle, m.id)}><i className="fa-solid fa-eye" /></button>
                                                                {f.editable && (
                                                                    <button type="button" className="action-btn"
                                                                            title={m.utilise_dans.length ? 'Choisi par un processus : il ne se supprime pas' : 'Supprimer ce modèle'}
                                                                            disabled={!modifiable || !!edition || m.utilise_dans.length > 0}
                                                                            onClick={() => setASupprimer({ famille: f.cle, modele: m })}><i className="fa-solid fa-trash" /></button>
                                                                )}
                                                            </td>
                                                        </tr>
                                                    ))}
                                                </tbody>
                                            </table>
                                        </>
                                    )}
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {repartir && (
                <StageModale titre={`Repartir d'un autre modèle — ${repartir.libelle}`} icone="fa-rotate-left" enCours={enCours}
                             libelleValider="Remplacer" validerDesactive={!repartir.choix} onValider={validerRepartir} onFermer={() => setRepartir(null)}>
                    <p style={{ fontSize: 12 }}>Le modèle de <b>« {docs?.processus?.titre} »</b> sera remplacé par une copie de :</p>
                    <div className="l2-choix">
                        {[['Modèles d\'origine du département', repartir.sources.filter((s) => s.origine)],
                          ['Celui d\'un autre processus', repartir.sources.filter((s) => !s.origine)]].map(([titre, liste]) => liste.length > 0 && (
                            <React.Fragment key={titre}>
                                <div className="l2-choix-titre">{titre}</div>
                                {liste.map((s) => (
                                    <label key={s.id}>
                                        <input type="radio" name="l2-repartir" checked={repartir.choix === s.id} onChange={() => setRepartir((r) => ({ ...r, choix: s.id }))} />
                                        <span>
                                            <b>{s.origine ? s.nom : `« ${s.processus} »`}</b>{s.annee && <> <span className="pf-chip">{s.annee}</span></>}
                                            <small>{s.origine ? `${s.resume} — tel que fourni au département (non modifiable directement)` : `${s.nom} · ${s.resume}`}</small>
                                        </span>
                                    </label>
                                ))}
                            </React.Fragment>
                        ))}
                    </div>
                    <div className="stg-encart stg-encart-alerte" style={{ marginTop: 6 }}>
                        <i className="fa-solid fa-triangle-exclamation" />
                        <span>Les modifications faites dans le modèle de ce processus seront perdues. Les fiches <b>déjà envoyées</b> gardent la version reçue (figée au premier envoi).</span>
                    </div>
                </StageModale>
            )}

            {aSupprimer && (
                <StageModale titre="Supprimer ce modèle" icone="fa-trash" ton="danger" enCours={enCours}
                             libelleValider="Supprimer" onValider={supprimer} onFermer={() => setASupprimer(null)}>
                    <p>Le modèle <b>« {aSupprimer.modele.nom} »</b> sera supprimé définitivement.</p>
                    <p style={{ color: 'var(--stg-muted)', fontSize: 12 }}>Aucun processus ne le choisit : aucune campagne n'est touchée.</p>
                </StageModale>
            )}
        </>
    );
}

// Lot 3.S-b2 (SM8) : la page d aperçu des liens d essai réutilise EXACTEMENT les mêmes données fictives.
export { portailFictif, ficheFictive, pageFictive };
export default StageParametrageModeles;
